import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useTableContext } from 'components/Table/Table';

import {
  savedViewsApi,
  useCreateSavedViewMutation,
  useDeleteSavedViewMutation,
  useGetSavedViewsQuery,
  useUpdateSavedViewMutation,
} from 'api/saved-views-api';

import { savedViewFilterSignaturesMatch, stripTransientQueryParams } from './savedViewQueryParams';

const REPORTING_TAB_TO_TYPE = {
  checks: 'check',
  candidates: 'candidate',
  referees: 'referee',
};

export const useSavedViews = tab => {
  const dispatch = useDispatch();
  const viewType = REPORTING_TAB_TO_TYPE[tab] ?? tab;
  const { data: rawSavedViews = [], isSuccess, isError } = useGetSavedViewsQuery();
  const savedViewsReady = isSuccess || isError;
  const [createSavedViewMutation] = useCreateSavedViewMutation();
  const [updateSavedViewMutation] = useUpdateSavedViewMutation();
  const [deleteSavedViewMutation] = useDeleteSavedViewMutation();

  const savedViews = useMemo(
    () =>
      rawSavedViews
        .filter(v => v.type === viewType)
        .map(v => {
          const { _pills, ...restParams } = v.query_params ?? {};
          return {
            id: v.id,
            name: v.name,
            type: v.type,
            isDefault: !!v.is_default,
            createdAt: v.created_at,
            savedQueryParams: stripTransientQueryParams(restParams),
            filterPills: _pills ?? [],
          };
        }),
    [rawSavedViews, viewType],
  );

  const defaultViewStableRef = useRef(null);
  const defaultView = useMemo(() => {
    const next = savedViews.find(v => v.isDefault) ?? null;
    const prev = defaultViewStableRef.current;
    if (next == null) {
      defaultViewStableRef.current = null;
      return null;
    }
    if (
      prev &&
      Number(prev.id) === Number(next.id) &&
      prev.name === next.name &&
      savedViewFilterSignaturesMatch(prev.savedQueryParams, next.savedQueryParams) &&
      JSON.stringify(prev.filterPills ?? []) === JSON.stringify(next.filterPills ?? [])
    ) {
      return prev;
    }
    defaultViewStableRef.current = next;
    return next;
  }, [savedViews]);

  const [activeViewId, setActiveViewId] = useState(null);
  const activeViewIdRef = useRef(activeViewId);
  activeViewIdRef.current = activeViewId;
  const [pendingApply, setPendingApply] = useState(null);
  const [saveViewModal, setSaveViewModal] = useState(false);
  const [manageViewsOpen, setManageViewsOpen] = useState(false);
  const defaultViewApplyOnceRef = useRef(false);
  const [bannerViewQuerySynced, setBannerViewQuerySynced] = useState(true);

  const handleSave = useCallback(
    async ({ viewName, filterPills, savedQueryParams }) => {
      const storedParams = stripTransientQueryParams(savedQueryParams);
      const created = await createSavedViewMutation({
        name: viewName,
        type: viewType,
        query_params: { ...storedParams, _pills: filterPills ?? [] },
      }).unwrap();
      if (created?.id != null) {
        dispatch(
          savedViewsApi.util.updateQueryData('getSavedViews', undefined, draft => {
            if (!Array.isArray(draft)) return;
            if (draft.some(v => String(v.id) === String(created.id))) return;
            draft.push(created);
          }),
        );
        setActiveViewId(Number(created.id));
      }
    },
    [createSavedViewMutation, dispatch, viewType],
  );

  const handleUpdate = useCallback(
    async (currentFilterPills, currentQueryParams) => {
      if (activeViewId == null) return;
      const storedParams = stripTransientQueryParams(currentQueryParams);
      await updateSavedViewMutation({
        id: activeViewId,
        query_params: { ...storedParams, _pills: currentFilterPills },
      }).unwrap();
    },
    [activeViewId, updateSavedViewMutation],
  );

  const handleDelete = useCallback(
    async viewId => {
      await deleteSavedViewMutation(viewId).unwrap();
      if (Number(activeViewIdRef.current) === Number(viewId)) {
        setActiveViewId(null);
        setPendingApply(null);
      }
    },
    [deleteSavedViewMutation],
  );

  const handleEditView = useCallback(
    async (viewId, payload) => {
      await updateSavedViewMutation({ id: viewId, ...payload }).unwrap();
    },
    [updateSavedViewMutation],
  );

  const handleSetDefault = useCallback(
    async (viewId, isDefault) => {
      await updateSavedViewMutation({ id: viewId, is_default: isDefault }).unwrap();
    },
    [updateSavedViewMutation],
  );

  return useMemo(
    () => ({
      savedViews,
      savedViewsReady,
      defaultView,
      activeViewId,
      setActiveViewId,
      pendingApply,
      setPendingApply,
      saveViewModal,
      setSaveViewModal,
      manageViewsOpen,
      setManageViewsOpen,
      handleSave,
      handleUpdate,
      handleDelete,
      handleEditView,
      handleSetDefault,
      defaultViewApplyOnceRef,
      bannerViewQuerySynced,
      setBannerViewQuerySynced,
    }),
    [
      savedViews,
      savedViewsReady,
      defaultView,
      activeViewId,
      pendingApply,
      saveViewModal,
      manageViewsOpen,
      handleSave,
      handleUpdate,
      handleDelete,
      handleEditView,
      handleSetDefault,
      bannerViewQuerySynced,
    ],
  );
};

const DEFAULT_VIEW_ID_UNSET = '__default_view_id_unset__';

export const ReportingTableViewSync = ({
  savedViewsReady,
  activeViewId,
  setActiveViewId,
  savedViews,
  defaultView,
  defaultAppliedRef,
  setBannerViewQuerySynced,
  pendingApply,
}) => {
  const { replaceAndApply, queryParams } = useTableContext();
  const replaceRef = useRef(replaceAndApply);
  replaceRef.current = replaceAndApply;
  const snapshotRef = useRef({});
  snapshotRef.current = {
    activeViewId,
    setActiveViewId,
    savedViews,
    defaultView,
    defaultAppliedRef,
  };

  const savedViewIdsKey = useMemo(
    () =>
      savedViews
        .map(v => String(v.id))
        .sort()
        .join(','),
    [savedViews],
  );

  const defaultViewIdDep =
    defaultView != null && defaultView.id != null ? Number(defaultView.id) : null;

  const appliedForMountRef = useRef(false);
  const prevDefaultViewIdRef = useRef(DEFAULT_VIEW_ID_UNSET);
  const prevActiveViewIdForApplyRef = useRef();
  const skipNextActiveViewChangeResetRef = useRef(false);

  useLayoutEffect(() => {
    if (prevActiveViewIdForApplyRef.current !== activeViewId) {
      prevActiveViewIdForApplyRef.current = activeViewId;
      if (skipNextActiveViewChangeResetRef.current) {
        skipNextActiveViewChangeResetRef.current = false;
      } else {
        appliedForMountRef.current = false;
      }
    }

    if (!savedViewsReady) return;

    const snap = snapshotRef.current;
    const defaultId =
      snap.defaultView != null && snap.defaultView.id != null ? Number(snap.defaultView.id) : null;

    if (prevDefaultViewIdRef.current !== DEFAULT_VIEW_ID_UNSET) {
      const prevDefaultId = prevDefaultViewIdRef.current;
      if (prevDefaultId !== defaultId && snap.activeViewId == null) {
        if (snap.defaultAppliedRef) snap.defaultAppliedRef.current = false;
        appliedForMountRef.current = false;
      }
    }
    prevDefaultViewIdRef.current = defaultId;

    let replacedForBanner = false;
    let activeId = snap.activeViewId;

    if (activeId != null) {
      const view = snap.savedViews.find(v => Number(v.id) === Number(activeId));
      if (view) {
        if (!appliedForMountRef.current) {
          const params = view.savedQueryParams ?? {};
          if (pendingApply) {
            appliedForMountRef.current = true;
          } else if (savedViewFilterSignaturesMatch(queryParams, params)) {
            appliedForMountRef.current = true;
          } else {
            appliedForMountRef.current = true;
            replaceRef.current(params);
            replacedForBanner = true;
          }
        }
        if (replacedForBanner) setBannerViewQuerySynced?.(false);
        return;
      }
      snap.setActiveViewId(null);
      activeId = null;
      if (snap.defaultAppliedRef) {
        snap.defaultAppliedRef.current = false;
      }
      appliedForMountRef.current = false;
    }

    const ref = snap.defaultAppliedRef;
    if (activeId == null && snap.defaultView && ref && !ref.current) {
      ref.current = true;
      appliedForMountRef.current = true;
      replaceRef.current(snap.defaultView.savedQueryParams ?? {});
      skipNextActiveViewChangeResetRef.current = true;
      snap.setActiveViewId(snap.defaultView.id);
      replacedForBanner = true;
    }

    if (replacedForBanner) setBannerViewQuerySynced?.(false);
  }, [
    savedViewsReady,
    savedViewIdsKey,
    defaultViewIdDep,
    activeViewId,
    pendingApply,
    queryParams,
    setBannerViewQuerySynced,
  ]);

  useEffect(() => {
    if (!setBannerViewQuerySynced) return;
    if (!savedViewsReady || activeViewId == null) {
      setBannerViewQuerySynced(true);
      return;
    }
    const view = savedViews.find(v => Number(v.id) === Number(activeViewId));
    if (!view?.savedQueryParams) {
      setBannerViewQuerySynced(true);
      return;
    }
    if (savedViewFilterSignaturesMatch(queryParams, view.savedQueryParams)) {
      setBannerViewQuerySynced(true);
    }
  }, [savedViewsReady, activeViewId, savedViews, queryParams, setBannerViewQuerySynced]);

  return null;
};

export const ActiveViewApplier = ({ pendingApply, setPendingApply, setBannerViewQuerySynced }) => {
  const { replaceAndApply } = useTableContext();
  const replaceRef = useRef(replaceAndApply);
  replaceRef.current = replaceAndApply;
  const setPendingRef = useRef(setPendingApply);
  setPendingRef.current = setPendingApply;
  const setBannerRef = useRef(setBannerViewQuerySynced);
  setBannerRef.current = setBannerViewQuerySynced;
  const lastApplied = useRef(null);

  useLayoutEffect(() => {
    if (!pendingApply) {
      lastApplied.current = null;
      return;
    }
    if (pendingApply !== lastApplied.current) {
      lastApplied.current = pendingApply;
      setBannerRef.current?.(false);
      replaceRef.current(pendingApply.params ?? {});
      setPendingRef.current?.(null);
    }
  }, [pendingApply]);

  return null;
};
